import { zodResolver } from "@hookform/resolvers/zod"
import { Loader2Icon } from "lucide-react"
import type * as React from "react"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import { CompanyUpdateSchema, GST_STATE_CODE_LIST } from "@steelbill/types"
import {
  useCompany,
  useSaveCompany,
  type CompanySettings,
} from "@/features/settings/use-company"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { NativeSelect, NativeSelectOption } from "@/components/ui/native-select"
import { Skeleton } from "@/components/ui/skeleton"
import { Textarea } from "@/components/ui/textarea"
import { useAuth } from "@/features/auth/auth-context"
import { ApiError } from "@/lib/api-error"

/**
 * Settings — the company identity every invoice prints (§7.3, §9.2).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS SCREEN IS WHY THE GSTIN IS NOT IN THE REPO. CLAUDE.md's rule — "GSTIN,
 * PAN and bank details never enter the repo; they are entered via ADMIN
 * settings and live in the database" — only works if the settings screen
 * exists. It shipped as a placeholder, `endpoints.company` had zero callers,
 * and every invoice printed "SELLER GSTIN:" followed by nothing: a document a
 * GST officer would not accept and Lupin could not claim input credit against.
 * The 2026-08-15 billing audit ranked it the worst gap in the app.
 *
 * ONE CAPABILITY, ONE AUDIENCE, ONE SUBJECT (§2.1). The company form needs
 * `settings.write` — ADMIN only. The OWNER sees the values read-only rather than
 * a door he cannot open; §2 says he "cannot change company/tax settings", not
 * that he may not know what they are.
 *
 * THE COUNTERS CARD IS NOT HERE, AND THE REASON IS §6.1. `routes/index.tsx`
 * guards `/settings` on `settings.write`, so an `invoice.set_counter` check
 * INSIDE this file could never fire for the OWNER — he was redirected Home
 * before the card rendered, which is precisely the collapse §6.1 forbids,
 * arrived at through routing rather than through the capability map.
 *
 * It moved to `features/settings/CountersCard.tsx` with its own screen, and
 * until 2026-08-17 was ALSO rendered here so the ADMIN found it where he already
 * was. That second mount is now gone: it is a section of the settings menu, one
 * click away, so rendering it here as well put the same number-changing form on
 * screen twice.
 *
 * THE SAME ZOD SCHEMAS THE SERVER VALIDATES WITH, imported from
 * `@steelbill/types` — the message beside a field is the message the API would
 * produce, and the two cannot drift.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function SettingsScreen(): React.JSX.Element {
  const { t } = useTranslation()
  const company = useCompany()

  return (
    <div className="flex w-full flex-col gap-4 p-4 md:gap-6 md:p-6">
      {/*
       * THE HEADING NAMES THE SECTION, NOT THE AREA.
       *
       * It read `nav.settings` — "Settings" — until 2026-08-17, which was right
       * when this screen WAS the settings area. It is now one section of five
       * behind a NavigationMenu (SettingsLayout), and the word "Settings"
       * already appears twice above it: once in the site header, once as the
       * area the menu belongs to. A third made the page name itself while the
       * highlighted menu item beside it said something else.
       *
       * `settings.businessTitle` is the SAME KEY the menu item uses
       * (features/settings/settings-sections.ts), so the heading and the
       * highlighted item cannot drift apart — the same trick the sidebar rows
       * have always used with their `nav.*` keys.
       */}
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t("settings.businessTitle")}</h1>
        <p className="text-muted-foreground text-sm">{t("settings.subtitle")}</p>
      </header>

      {company.isPending ? (
        <div className="flex flex-col gap-4">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      ) : company.isError ? (
        <Alert variant="destructive">
          <AlertTitle>{t("err.somethingWentWrong")}</AlertTitle>
          <AlertDescription>
            {company.error instanceof ApiError ? company.error.message : String(company.error)}
          </AlertDescription>
        </Alert>
      ) : company.data === undefined ? (
        // Settled but empty — an API answering an unexpected envelope. A crash
        // here would take the whole screen; a skeleton says "still nothing".
        <Skeleton className="h-48 w-full" />
      ) : (
        <CompanyForm company={company.data} />
      )}
      {/*
       * ═══════════════════════════════════════════════════════════════════════
       * THIS SCREEN IS THE BUSINESS DETAILS AND NOTHING ELSE, SINCE 2026-08-17.
       *
       * It used to carry two passengers, both of which are now SECTIONS OF THE
       * SETTINGS MENU in their own right (features/settings/SettingsLayout.tsx):
       *
       *   - `<CountersCard>`, which also rendered on its own screen. The same
       *     form in two places is not a convenience — it is a second copy of a
       *     control that re-issues invoice numbers, and the confirmation switch
       *     or the direction warning going missing from one of them is a bug
       *     nobody would see until a number came back.
       *   - An "Open Features" button, added when Features was reachable only by
       *     knowing the URL. It is now the third item in the menu directly above
       *     this heading, so the button pointed at something already on screen.
       *
       * The reasoning that put each here still holds; the menu answers it
       * better. `flags.moduleOffHelp` tells its reader to look in "Settings,
       * under Features", and that sentence now finds a menu item rather than a
       * button buried under the bank details — which was the original worry,
       * that a switch capable of taking billing down sat where somebody is
       * scrolling to fix an IFSC.
       * ═══════════════════════════════════════════════════════════════════════
       */}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* The company form                                                            */
/* -------------------------------------------------------------------------- */

/**
 * The state a GSTIN's first two characters name, or `null` if they name none.
 *
 * §4 — the leading pair IS the state code, which is why the picker locks once a
 * GSTIN is typed. `null` rather than a guess: a half-typed or foreign-looking
 * pair should show itself, not the first state in the list.
 */
function derivedStateName(gstin: string): string | null {
  const code = gstin.slice(0, 2)
  return GST_STATE_CODE_LIST.find((state) => state.code === code)?.name ?? null
}

function CompanyForm({ company }: { company: CompanySettings }): React.JSX.Element {
  const { t } = useTranslation()
  const { can } = useAuth()
  const save = useSaveCompany()
  // Read-only for the OWNER rather than hidden — see the screen note.
  const editable = can("settings.write")

  /*
   * THE CAST ON THE RESOLVER IS A TYPE-LEVEL COMPROMISE, NOT A RUNTIME ONE.
   * `clearable()` wraps its schema in `z.preprocess` (so "" clears a field to
   * null — see primitives.ts), and preprocess makes zod's INPUT type `unknown`.
   * `zodResolver` therefore infers a form shape of `unknown`s that cannot match
   * the concrete strings the inputs produce. The schema itself still runs
   * unchanged on every submit — the exact one `PUT /company` validates with —
   * so the messages and the acceptance rules cannot drift; only the static
   * inference is papered over.
   */
  const form = useForm<CompanyFormValues>({
    resolver: zodResolver(CompanyUpdateSchema) as never,
    mode: "onTouched",
    defaultValues: toFormValues(company),
  })

  // Refill when the row changes under us (another device saved). `reset` keeps
  // the dirty state honest, but never while HE is mid-edit — that would
  // silently discard what he typed.
  useEffect(() => {
    if (!form.formState.isDirty) form.reset(toFormValues(company))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company])

  const gstin = (form.watch("gstin") ?? "").trim()

  const onSubmit = form.handleSubmit((values) => {
    save.mutate(values as Record<string, unknown>, {
      onSuccess: (result) => {
        toast.success(t("msg.saved", { defaultValue: t("status.saved") }))
        for (const warning of result.warnings ?? []) {
          toast.warning(warning, { duration: Infinity, closeButton: true })
        }
        form.reset(toFormValues(result.data))
      },
      onError: (error) => {
        toast.error(error instanceof ApiError ? error.message : t("err.somethingWentWrong"))
      },
    })
  })

  const field = (name: keyof ReturnType<typeof toFormValues>) => ({
    ...form.register(name),
    disabled: !editable,
    "aria-invalid": form.formState.errors[name] !== undefined,
  })

  const errorOf = (name: keyof ReturnType<typeof toFormValues>) => {
    const error = form.formState.errors[name]
    return error?.message === undefined ? null : (
      <p className="text-destructive text-xs">{String(error.message)}</p>
    )
  }

  return (
    <form onSubmit={(event) => void onSubmit(event)} className="flex flex-col gap-4 md:gap-6">
      {/*
        * THE BANNER THIS SCREEN EXISTS FOR. While the GSTIN is blank, every
        * invoice is printing without a seller GSTIN and is not a valid tax
        * invoice (§9.2). Said here, at the place it is fixed, in words — the
        * editor and the save path warn too, but this is where the field is.
        */}
      {gstin === "" ? (
        <Alert>
          <AlertTitle>{t("settings.gstinMissingTitle")}</AlertTitle>
          <AlertDescription>{t("settings.gstinMissingHelp")}</AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>{t("settings.businessTitle")}</CardTitle>
          <CardDescription>{t("settings.businessIntro")}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="legalName">{t("settings.legalName")}</Label>
            <Input id="legalName" {...field("legalName")} />
            <p className="text-muted-foreground text-xs">{t("settings.legalNameHelp")}</p>
            {errorOf("legalName")}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tradeName">{t("settings.tradeName")}</Label>
            <Input id="tradeName" {...field("tradeName")} />
            {errorOf("tradeName")}
          </div>
          <div className="flex flex-col gap-1.5 md:col-span-2">
            <Label htmlFor="tagline">{t("settings.tagline")}</Label>
            <Input id="tagline" {...field("tagline")} />
            {errorOf("tagline")}
          </div>
          <div className="flex flex-col gap-1.5 md:col-span-2">
            <Label htmlFor="addressLine1">{t("settings.addressLine1")}</Label>
            <Input id="addressLine1" {...field("addressLine1")} />
            {errorOf("addressLine1")}
          </div>
          <div className="flex flex-col gap-1.5 md:col-span-2">
            <Label htmlFor="addressLine2">{t("settings.addressLine2")}</Label>
            <Input id="addressLine2" {...field("addressLine2")} />
            {errorOf("addressLine2")}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="city">{t("settings.city")}</Label>
            <Input id="city" {...field("city")} />
            {errorOf("city")}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pincode">{t("settings.pincode")}</Label>
            <Input id="pincode" inputMode="numeric" {...field("pincode")} />
            {errorOf("pincode")}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="phone">{t("settings.phone")}</Label>
            <Input id="phone" inputMode="tel" {...field("phone")} />
            {errorOf("phone")}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="email">{t("settings.email")}</Label>
            <Input id="email" inputMode="email" {...field("email")} />
            {errorOf("email")}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("settings.identifiersTitle")}</CardTitle>
          <CardDescription>{t("settings.identifiersIntro")}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="gstin">{t("settings.gstin")}</Label>
            <Input id="gstin" autoCapitalize="characters" {...field("gstin")} />
            <p className="text-muted-foreground text-xs">{t("settings.gstinHelp")}</p>
            {errorOf("gstin")}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pan">{t("settings.pan")}</Label>
            <Input id="pan" autoCapitalize="characters" {...field("pan")} />
            {errorOf("pan")}
          </div>
          <div className="flex flex-col gap-1.5 md:col-span-2">
            <Label htmlFor="stateCode">{t("lbl.state")}</Label>
            {/*
              * Once a GSTIN exists its first two characters ARE the state (§4)
              * and the server ignores this field — so it locks, and the help
              * line says why rather than leaving a mysteriously dead control.
              */}
            {/*
              * ─────────────────────────────────────────────────────────────
              * THE ONE SELECT IN THE APP THAT STAYS NATIVE. THIS IS A
              * DECISION, NOT AN OVERSIGHT — DO NOT "FINISH THE MIGRATION".
              *
              * Every other <select> in the app is now the shadcn `Select`
              * from `components/ui/select`, because the owner asked for
              * visual consistency and that is his call. This one is the
              * exception, and four things compound to make it so:
              *
              *  1. LENGTH. `GST_STATE_CODE_LIST` is 37 entries. At the 44px
              *     touch target §11.1 requires — the condition the whole
              *     migration was granted under — that is ~1,630px of list,
              *     about two and a half phone screens inside a popup capped
              *     at `--available-height`. The OS wheel is already
              *     full-height and starts on the current value.
              *  2. WIDTH. The labels run to 44 characters ("26 — Dadra and
              *     Nagar Haveli and Daman and Diu") and `SelectContent` is
              *     `w-(--anchor-width)` with `overflow-x-hidden`. A half-read
              *     state name on the field that decides CGST+SGST versus
              *     IGST (§6) is a correctness risk, and a wrong split on an
              *     issued invoice is only correctable by a credit note
              *     (§17.3).
              *  3. COST. This is `{...field("stateCode")}` — react-hook-form
              *     `register` plus a DOUBLE conditional disable (`field()`
              *     supplies `!editable`, the JSX adds `gstin !== ""`). Base
              *     UI's root takes neither a register `onChange` nor its ref,
              *     so it needs a `Controller` with both disables re-attached
              *     by hand. Drop one and a locked GSTIN state becomes
              *     editable again.
              *  4. BENEFIT. Near zero. This is an ADMIN screen, at a desk,
              *     filled in once when the business is set up and then never
              *     again. The consistency argument is weakest exactly where
              *     the cost is highest.
              *
              * The customer-side state picker (CustomerEditorScreen) DID
              * migrate: it is edited far more often, and it carries the
              * widened popup and wrapped labels that answer (2). If this one
              * is ever converted, copy that treatment wholesale and cover
              * the GSTIN lock with a test first.
              * ─────────────────────────────────────────────────────────────
              */}
            <NativeSelect id="stateCode" {...field("stateCode")} disabled={!editable || gstin !== ""}>
              {GST_STATE_CODE_LIST.map((state) => (
                <NativeSelectOption key={state.code} value={state.code}>
                  {state.code} — {state.name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
            {/*
              * `{{state}}` AND `{{code}}` ARE SUPPLIED. This line was called
              * bare against "State: {{state}} ({{code}}) — taken from the
              * GSTIN.", and i18next's `skipOnVariables` defaults to TRUE, so it
              * rendered the braces verbatim: the ADMIN was told the state came
              * from the GSTIN and never told WHICH state it came out as — the
              * one fact the helper exists to confirm, under a control that is
              * locked precisely because the GSTIN decides it.
              *
              * Derived from the GSTIN's own first two characters (§4), NOT from
              * the `stateCode` field, because the sentence claims the GSTIN is
              * the source and the server ignores the field while a GSTIN
              * exists. An unrecognised pair falls back to the code itself, so a
              * half-typed GSTIN shows "State: 27 (27)" rather than "undefined".
              */}
            {gstin === "" ? null : (
              <p className="text-muted-foreground text-xs">
                {t("settings.stateDerived", {
                  state: derivedStateName(gstin) ?? gstin.slice(0, 2),
                  code: gstin.slice(0, 2),
                })}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("settings.bankTitle")}</CardTitle>
          <CardDescription>{t("settings.bankIntro")}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="bankName">{t("settings.bankName")}</Label>
            <Input id="bankName" {...field("bankName")} />
            {errorOf("bankName")}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="bankBranch">{t("settings.bankBranch")}</Label>
            <Input id="bankBranch" {...field("bankBranch")} />
            {errorOf("bankBranch")}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="bankAccountNo">{t("settings.bankAccountNo")}</Label>
            <Input id="bankAccountNo" inputMode="numeric" {...field("bankAccountNo")} />
            {errorOf("bankAccountNo")}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="bankIfsc">{t("settings.bankIfsc")}</Label>
            <Input id="bankIfsc" autoCapitalize="characters" {...field("bankIfsc")} />
            {errorOf("bankIfsc")}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("settings.printedTitle")}</CardTitle>
          <CardDescription>{t("settings.printedIntro")}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="termsDefault">{t("settings.termsDefault")}</Label>
            <Textarea id="termsDefault" rows={3} {...field("termsDefault")} />
            <p className="text-muted-foreground text-xs">{t("settings.termsDefaultHelp")}</p>
            {errorOf("termsDefault")}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="declarationDefault">{t("settings.declaration")}</Label>
            <Textarea id="declarationDefault" rows={2} {...field("declarationDefault")} />
            {errorOf("declarationDefault")}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="jurisdictionNote">{t("settings.jurisdiction")}</Label>
            <Input id="jurisdictionNote" {...field("jurisdictionNote")} />
            {errorOf("jurisdictionNote")}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="computerGeneratedNote">{t("settings.computerGenerated")}</Label>
            <Input id="computerGeneratedNote" {...field("computerGeneratedNote")} />
            {errorOf("computerGeneratedNote")}
          </div>
        </CardContent>
      </Card>

      {editable ? (
        <div className="flex justify-end">
          <Button type="submit" disabled={save.isPending || !form.formState.isDirty}>
            {save.isPending ? <Loader2Icon className="animate-spin" /> : null}
            {t("btn.save")}
          </Button>
        </div>
      ) : (
        <p className="text-muted-foreground text-sm">{t("settings.readOnly")}</p>
      )}
    </form>
  )
}

type CompanyFormValues = ReturnType<typeof toFormValues>

/** The row → the form. `undefined` for empty so zod's optionals behave. */
function toFormValues(company: CompanySettings) {
  return {
    legalName: company.legalName,
    tradeName: company.tradeName ?? undefined,
    tagline: company.tagline ?? undefined,
    gstin: company.gstin ?? undefined,
    pan: company.pan ?? undefined,
    addressLine1: company.addressLine1,
    addressLine2: company.addressLine2 ?? undefined,
    city: company.city,
    stateCode: company.stateCode,
    pincode: company.pincode,
    phone: company.phone,
    email: company.email,
    bankName: company.bankName ?? undefined,
    bankAccountNo: company.bankAccountNo ?? undefined,
    bankIfsc: company.bankIfsc ?? undefined,
    bankBranch: company.bankBranch ?? undefined,
    termsDefault: company.termsDefault ?? undefined,
    jurisdictionNote: company.jurisdictionNote ?? undefined,
    computerGeneratedNote: company.computerGeneratedNote ?? undefined,
    declarationDefault: company.declarationDefault ?? undefined,
  }
}

